import { EventsMonitorApiService } from './events-monitor.api.service';
import { inject, Injectable } from '@angular/core';

import { catchError, map, Observable, of } from 'rxjs';
import {
  ChartInteractionState,
  EventMonitorDeviceStatus,
  EventMonitorFilterName,
  EventMonitorTableSortName,
  EventsMonitorCountResponse,
  EventsMonitorDurationGroupResponse,
  EventsMonitorEventGroupResponse,
  EventsMonitorPayload,
  EventsMonitorSeveritiesResponse,
  EventsMonitorTrendsPayload,
  EventsMonitorTrendsResponse,
  EventsTable,
  UserConfigEventMonitorFilters
} from '../events-monitor.types';
import { DevicesPayload, Filter } from '../../../equipment/devices/components/devices.models';
import { mapLink, notApplicable } from '../../../../shared/constants';
import { MapValidator } from '../../../../shared/components/listing-page/helpers/map-validator';
import { ReportGroupFilterResponse } from '../../../../services/api/report-group-filter/report-group-filter-api.types';
import { ReportGroupFilterApiService } from '../../../../services/api/report-group-filter/report-group-filter-api.service';
import { TimezoneService } from 'src/app/services/utility/timezone.service';
import { multiplierWithUom } from '../../../../shared/utils';
import { UserConfigService } from 'src/app/services/utility/user-config.service';
import { createDefaultDateRange } from '../events-monitor.utils';
import { resolveDateRangeFromPreset } from '../../../../shared/components/datepicker/datepicker-preset.utils';
import { DurationPipe } from '../../../../shared/pipes/duration.pipe';

@Injectable({ providedIn: 'root' })
export class EventsMonitorService {
  private eventsApiService = inject(EventsMonitorApiService);
  private reportGroupFilterApiService = inject(ReportGroupFilterApiService);

  private readonly tableState = { data: [], total: 0 };
  private readonly timezoneService = inject(TimezoneService);
  readonly #userConfigService = inject(UserConfigService);
  readonly #storageKey = 'events-monitor';
  readonly #durationPipe = new DurationPipe();

  getTableData(tableState: DevicesPayload): Observable<EventsTable> {
    return this.eventsApiService.getAll(tableState).pipe(
      map(events => {
        const { items, totalCount } = events;
        const data = items.map(event => {
          return {
            ...event,
            status:
              event.status === EventMonitorDeviceStatus.Active
                ? EventMonitorDeviceStatus.Active
                : EventMonitorDeviceStatus.Cleared,
            lastDetectedUtc: this.timezoneService.formatLong(event.lastDetectedUtc),
            lastDetectedUtcOriginal: event.lastDetectedUtc,
            startedAtUtc: this.timezoneService.formatLong(event.startedAtUtc),
            startedAtUtcOriginal: event.startedAtUtc,
            endedAtUtc: event.endedAtUtc ? this.timezoneService.formatLong(event.endedAtUtc) : null,
            endedAtUtcOriginal: event.endedAtUtc,
            commodity: event.commodity === 'All' ? notApplicable : event.commodity,
            deviceType:
              event.deviceType === 'All'
                ? 'All Devices'
                : event.deviceType === 'Unknown'
                  ? notApplicable
                  : event.deviceType,
            location: event.geoMetaData && MapValidator.isValidCoords(event.geoMetaData?.latitude) ? mapLink : null,
            unitOfMeasure: multiplierWithUom(event.multiplier, event.unitOfMeasure),
            duration: this.#durationPipe.transform(event.lastDetectedUtc, event.endedAtUtc)
          };
        });
        return { data, total: totalCount };
      }),
      catchError(() => of(this.tableState))
    );
  }

  getWidgetTotalCount(
    tableState: Pick<EventsMonitorPayload, 'filters' | 'search'>
  ): Observable<EventsMonitorCountResponse> {
    return this.eventsApiService.getWidgetTotalCount(tableState);
  }

  getWidgetEventGroup(
    tableState: Pick<EventsMonitorPayload, 'filters' | 'search'>
  ): Observable<EventsMonitorEventGroupResponse[]> {
    return this.eventsApiService.getWidgetEventGroup(tableState);
  }

  getWidgetDurationGroup(
    tableState: Pick<EventsMonitorPayload, 'filters' | 'search'>
  ): Observable<EventsMonitorDurationGroupResponse[]> {
    return this.eventsApiService.getWidgetDurationGroup(tableState);
  }

  getWidgetSeverity(
    tableState: Pick<EventsMonitorPayload, 'filters' | 'search'>
  ): Observable<EventsMonitorSeveritiesResponse[]> {
    return this.eventsApiService.getWidgetSeverity(tableState);
  }

  getTrends(trendsPayload: EventsMonitorTrendsPayload): Observable<EventsMonitorTrendsResponse> {
    return this.eventsApiService.getTrends(trendsPayload);
  }

  getReportGroup(applicable: string): Observable<ReportGroupFilterResponse[]> {
    return this.reportGroupFilterApiService.getReportGroup(applicable);
  }

  getShiftedDatePayload(
    filters: Filter[],
    isForTrendsAll: boolean = false,
    chartState: ChartInteractionState | undefined = undefined
  ): Filter[] {
    if (!filters?.length) {
      return [];
    }
    const aggregatedFilters = [...filters];
    const isSeverityFilterExist = filters.find(filter => filter.filterType === EventMonitorFilterName.Severity);
    if (!isSeverityFilterExist && chartState?.selectedSeries.length) {
      aggregatedFilters.push({
        filterType: EventMonitorFilterName.Severity,
        filteredValues: [...chartState.selectedSeries]
      });
    }
    return aggregatedFilters.map(filter => {
      if (filter.filterType === EventMonitorTableSortName.LastDetectedUtc) {
        const [start, end] = chartState?.selectedSeries.length
          ? [chartState.startDate, chartState.endDate]
          : filter.filteredValues;

        return {
          filterType: isForTrendsAll
            ? EventMonitorTableSortName.AllActiveDateUtc
            : EventMonitorTableSortName.LastDetectedUtc,
          filteredValues: [start, end]
        };
      }

      if (filter.filterType === EventMonitorFilterName.Severity) {
        const severity = chartState?.selectedSeries.length ? chartState.selectedSeries : filter.filteredValues;

        return {
          filterType: EventMonitorFilterName.Severity,
          filteredValues: [...severity]
        };
      }
      return filter;
    });
  }

  persistFilters(state: UserConfigEventMonitorFilters): void {
    this.#userConfigService.saveFilters(this.#storageKey, state);
  }

  loadPersistedFilters(): Observable<UserConfigEventMonitorFilters> {
    return this.#userConfigService
      .getGridSettings<UserConfigEventMonitorFilters>(this.#storageKey)
      .pipe(map(s => this.extractPersistedFilters(s?.filters ?? null)));
  }

  extractPersistedFilters(raw: UserConfigEventMonitorFilters | null): UserConfigEventMonitorFilters {
    const saved = raw?.dateRange ?? createDefaultDateRange();
    const dateRange = saved.presetId
      ? (resolveDateRangeFromPreset(saved.presetId) ?? { start: new Date(saved.start), end: new Date(saved.end) })
      : { start: new Date(saved.start), end: new Date(saved.end) };
    return {
      filters: raw?.filters ?? [],
      dateRange
    };
  }
}
